import {
    createStyles,
    Theme,
    Typography,
    withStyles,
    WithStyles
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import NumberFormat from "react-number-format";
import { DateTime } from "luxon";
import { useAppSelector } from "../../../store/hooks";
import { IParcelDetail } from "../../../models/parcel/IParcelDetail";

export const HIGHLIGHT_DURATION_MS = 15000;
export const FLASH_DURATION_MS = 300;

type BidAmountTextProps = WithStyles<typeof styles> & {
    completed: boolean;
    isLive: boolean;
    isParcelClosed?: boolean;
    parcel: IParcelDetail;
};

export const styles = (theme: Theme) =>
    createStyles({
        bidAmountText: {
            [theme.breakpoints.down("xs")]: { fontSize: 18 },
            color: "white",
            fontWeight: "bold"
        },
        bidAmountTextActive: {
            color: theme.palette.primary.main
        },
        bidAmountTextHighestBidder: { color: theme.palette.success.main },
        bidAmountTextActiveHighlight: {
            color: theme.palette.error.main,
            transition: "color 300ms linear"
        },
        bidAmountCompleted: {
            color: "#F5F5F5"
        },
        bidAmountWrapper: { display: "inline-block" },
        bidPopup: {
            [theme.breakpoints.down("sm")]: {
                paddingLeft: theme.spacing(3),
                paddingRight: theme.spacing(3)
            },
            backgroundColor: theme.palette.success.main,
            color: theme.palette.success.contrastText,
            display: "inline-block",
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2)
        },
        bidStatusText: {
            [theme.breakpoints.down("xs")]: { fontSize: 10 },
            display: "block",
            fontWeight: "bold",
            marginTop: 5,
            color: theme.palette.common.white
        }
    });

function BidAmountText({
    classes,
    completed,
    isLive,
    isParcelClosed,
    parcel
}: BidAmountTextProps) {
    const user = useAppSelector((state) => state.userStoreState.user);
    const parcelBidDict = useAppSelector(
        (state) => state.parcelBidStoreState.parcelBidDict
    );

    const [isHighlighted, setIsHighlighted] = useState(false);
    const [isFlashing, setIsFlashing] = useState(false);

    const parcelBids = parcelBidDict[parcel.parcelId] || [];
    const lastParcelBid = parcelBids[parcelBids.length - 1];

    const isUserCurrentHighestBidder =
        lastParcelBid && lastParcelBid.userId === user?.id;
    const isUserFinalHighestBidder =
        isUserCurrentHighestBidder && !isLive && completed;

    useEffect(() => {
        // If there are no bids or the user is the highest bidder, ensure the flashing and highlighting are off
        if (!lastParcelBid || isUserCurrentHighestBidder) {
            setIsFlashing(false);
            setIsHighlighted(false);
            return;
        }

        const lastBidTime = DateTime.fromISO(lastParcelBid.bidReceivedTime);
        const timeSinceLastBid = DateTime.now().diff(lastBidTime).milliseconds;

        // Trigger flash effect
        setIsFlashing(true);
        const flashTimeout = setTimeout(() => {
            setIsFlashing(false);
        }, FLASH_DURATION_MS);

        if (timeSinceLastBid < HIGHLIGHT_DURATION_MS) {
            // Delay setting highlight until after flash
            const highlightTimeout = setTimeout(() => {
                setIsHighlighted(true);
            }, FLASH_DURATION_MS);

            const clearHighlightTimeout = setTimeout(() => {
                setIsHighlighted(false);
            }, HIGHLIGHT_DURATION_MS - timeSinceLastBid);

            return () => {
                clearTimeout(flashTimeout);
                clearTimeout(highlightTimeout);
                clearTimeout(clearHighlightTimeout);
            };
        } else {
            setIsHighlighted(false);
            return () => clearTimeout(flashTimeout);
        }
    }, [lastParcelBid, isUserCurrentHighestBidder, parcelBids.length]);

    const bidStatusText =
        parcelBids.length > 0 ? "Current Bid" : "Starting Bid";

    const getBidValue = () => {
        // If the auction has not started yet, show the starting bid
        if (!isLive && !completed) {
            return parcel.startingBid;
        }

        // If the parcel is closed and there are no bids, hide the bid value
        if (isParcelClosed && parcelBids.length === 0) {
            return undefined;
        }

        // Else, show the override, last accepted bid, or starting bid
        return (
            parcel.lastAcceptedBidOverride ||
            parcel.lastAcceptedBid ||
            parcel.startingBid
        );
    };

    const getFontClasses = () => {
        let fontClasses = classes.bidAmountText;

        if (!completed) {
            if (isLive) {
                fontClasses = `${fontClasses} ${classes.bidAmountTextActive}`;

                if (!isFlashing && isHighlighted) {
                    fontClasses = `${fontClasses} ${classes.bidAmountTextActiveHighlight}`;
                }

                if (isUserCurrentHighestBidder) {
                    fontClasses = `${fontClasses} ${classes.bidAmountTextHighestBidder}`;
                }
            }
        } else {
            fontClasses = classes.bidAmountCompleted;
        }

        return fontClasses;
    };

    const bidValue = getBidValue();
    const fontClasses = getFontClasses();
    const bidTextId = `bid-text-${parcel.parcelId}`;

    return (
        <div className={classes.bidAmountWrapper}>
            {isUserFinalHighestBidder && (
                <Typography
                    className={classes.bidPopup}
                    component="div"
                    variant="caption"
                    display="block"
                >
                    Highest bidder!
                </Typography>
            )}
            <Typography
                component="h5"
                variant="h5"
                id={bidTextId}
                data-testid={bidTextId}
                className={fontClasses}
                display="block"
            >
                <NumberFormat
                    value={bidValue}
                    thousandSeparator={true}
                    prefix="$"
                    decimalScale={0}
                    fixedDecimalScale={true}
                    displayType="text"
                />
                {isLive && (
                    <Typography
                        className={classes.bidStatusText}
                        component="span"
                        variant="caption"
                        color="textPrimary"
                    >
                        {bidStatusText}
                    </Typography>
                )}
            </Typography>
        </div>
    );
}

export default withStyles(styles)(BidAmountText);
